import type { ReactNode } from 'react';

import { Table } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

import type { DataTableProps, DataTableRow } from '../types';
import { useDataTable } from '../model/use-data-table';
import { useHorizontalScroll } from '../model/use-horizontal-scroll';
import { DataTableHeader } from './table-header';
import { DataTableBody } from './table-body';
import { TableToolbar } from './table-toolbar';
import { InfinityScrollLoader } from './infinity-scroll-loader';
import { ColumnCustomizationDialog } from './column-customization-dialog';
import { FullscreenDialog } from './fullscreen-dialog';
import { TableScrollArea } from './table-scroll-area';
import { DataTableCards } from './data-table-cards';

export function DataTable<T extends DataTableRow>({
  data,
  // max-h, not h: the scroll area is a flex-1 item of an auto-height column, so its flex-basis overrides a height
  // and the area grows with the rows — the page scrolls instead of the table and the sticky header has nothing to stick to.
  height = 'max-h-[calc(100vh-200px)]',
  contentClassName,
  columns,
  title,
  hideToolbarTitle = false,
  total,
  helpTarget,
  tableId,
  isLoading = false,
  isFetchingNextPage = false,
  showToolbar = true,
  hasMore = false,
  onLoadMore,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  actions = [],
  pinActions = true,
  cardsOnMobile = false,
  sortField,
  sortDirection,
  onSort,
  getRowId = (row) => row.id,
  enableFullscreen = true,
  enableColumnCustomization = true,
  enableExport = false,
  onExport,
  isExporting = false,
  columnPresets = [],
  hierarchical = false,
  expandedRows,
  onRowExpand,
  getRowChildren,
  getRowHasChildren,
  getRowLevel,
  renderExpandIcon,
  rowClassName,
  isRowSelectable,
  renderGroupRow,
  isGroupRow
}: DataTableProps<T>) {
  const hasPinnedActions = pinActions && actions.length > 0;

  const {
    table,
    loadMoreRef,
    isCustomizeOpen,
    isFullscreenOpen,
    tempColumnConfigs,
    setIsCustomizeOpen,
    setIsFullscreenOpen,
    reorderColumns,
    toggleColumnVisibility,
    openColumnCustomization,
    saveColumnCustomization,
    cancelColumnCustomization,
    resetColumnCustomization,
    applyPreset,
    rollbackColumnCustomization
  } = useDataTable({
    data,
    columns,
    tableId,
    isFetchingNextPage,
    hasMore,
    onLoadMore,
    selectedIds,
    onSelectionChange,
    getRowId,
    sortField,
    sortDirection,
    onSort,
    hasPinnedActions
  });

  // One per scroll container: the inline table and the fullscreen copy scroll independently
  const inlineScroll = useHorizontalScroll();
  const fullscreenScroll = useHorizontalScroll();

  const renderTableContent = (isInFullscreen = false) => (
    <TableScrollArea
      scroll={isInFullscreen ? fullscreenScroll : inlineScroll}
      className={cn('relative overflow-auto flex-1', isInFullscreen ? 'h-full' : height)}>
      {/* The base Table wraps <table> in its own overflow-x-auto div; that inner scroll container
          would capture position:sticky of pinned cells while this outer div is what actually
          h-scrolls — neutralize it so pinned columns stick to the real scrollport */}
      <div
        className={cn(
          'inline-block min-w-full align-middle px-6 pb-4 [&_[data-slot=table-container]]:overflow-visible',
          contentClassName
        )}>
        {/* Separate borders, drawn by the cells: a collapsed border belongs to the table grid, so the sticky header
            and hierarchy rows left it behind and rows passing underneath showed through at their edges */}
        <Table
          className={cn(
            'w-full border-separate border-spacing-0 [&>thead>tr>th]:border-b [&>tbody>tr>td]:border-b [&>tbody>tr:last-child>td]:border-b-0',
            // Fixed layout: column widths come from the columns, not from the loaded rows, so a sort or the next page
            // does not move them. Everything wraps inside its cell — nowrap text and badges would spill over.
            'table-fixed [&>thead>tr>th]:whitespace-normal [&>tbody>tr>td]:whitespace-normal [&>tbody>tr>td_[data-slot=badge]]:max-w-full [&>tbody>tr>td_[data-slot=badge]]:whitespace-normal'
          )}>
          <DataTableHeader
            table={table}
            selectable={selectable}
            hasActions={actions.length > 0}
            pinActions={hasPinnedActions}
          />
          <DataTableBody
            table={table}
            selectable={selectable}
            actions={actions}
            pinActions={hasPinnedActions}
            hierarchical={hierarchical}
            expandedRows={expandedRows}
            onRowExpand={onRowExpand}
            getRowChildren={getRowChildren}
            getRowHasChildren={getRowHasChildren}
            getRowLevel={getRowLevel}
            renderExpandIcon={renderExpandIcon}
            rowClassName={rowClassName}
            isRowSelectable={isRowSelectable}
            renderGroupRow={renderGroupRow}
            isGroupRow={isGroupRow}
          />
        </Table>
        {isFullscreenOpen === isInFullscreen && (
          <InfinityScrollLoader loadMoreRef={loadMoreRef} hasMore={hasMore} isLoading={isFetchingNextPage} />
        )}
      </div>

      {isLoading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-10">
          <Spinner className="h-8 w-8" />
        </div>
      )}
    </TableScrollArea>
  );

  return (
    <section className="flex flex-col border border-border shadow-sm rounded-lg bg-card mb-4">
      {showToolbar && (
        <TableToolbar
          title={hideToolbarTitle ? undefined : title}
          total={total}
          helpTarget={helpTarget}
          enableColumnCustomization={enableColumnCustomization}
          enableExport={enableExport}
          enableFullscreen={enableFullscreen}
          onCustomizeColumns={openColumnCustomization}
          onExport={onExport}
          isExporting={isExporting}
          onFullscreen={() => setIsFullscreenOpen(true)}
          scroll={inlineScroll}
        />
      )}

      {cardsOnMobile ? (
        <CardsOnPhone
          cards={
            <DataTableCards table={table} selectable={selectable} isRowSelectable={isRowSelectable} actions={actions} />
          }
          table={renderTableContent()}
        />
      ) : (
        renderTableContent()
      )}

      <ColumnCustomizationDialog
        open={isCustomizeOpen}
        onOpenChange={setIsCustomizeOpen}
        columnConfigs={tempColumnConfigs}
        onReorderColumns={reorderColumns}
        onToggleColumnVisibility={toggleColumnVisibility}
        onSave={saveColumnCustomization}
        onCancel={cancelColumnCustomization}
        onReset={resetColumnCustomization}
        onRollback={rollbackColumnCustomization}
        onApplyPreset={applyPreset}
        presets={columnPresets}
      />

      <FullscreenDialog
        open={isFullscreenOpen}
        onOpenChange={setIsFullscreenOpen}
        title={title}
        scroll={fullscreenScroll}>
        {renderTableContent(true)}
      </FullscreenDialog>
    </section>
  );
}

/** Its own component so that only a table asking for cards watches the viewport. */
function CardsOnPhone({ cards, table }: { cards: ReactNode; table: ReactNode }) {
  return useIsMobile() ? cards : table;
}
